import React from 'react';
import { ReactFormInputOptions, useFormControl } from '@vendure/admin-ui/react';

const ACCEPTED_EXTENSIONS = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
const ACCEPTED_IMAGE = ['image/png', 'image/jpeg', 'image/gif', 'image/webp'];

const isAcceptedFile = (file: File): boolean => {
  if (ACCEPTED_IMAGE.includes(file.type)) return true;
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));
};

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

const IconUpload = ({ readonly }: ReactFormInputOptions) => {
  const { value, setFormValue } = useFormControl();
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [fileName, setFileName] = React.useState<string | null>(null);

  const base64Value: string = typeof value === 'string' ? value : '';

  const handlePick = () => {
    setError(null);
    inputRef.current?.click();
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset para permitir re-selecionar o mesmo ficheiro
    event.target.value = '';
    if (!file) return;

    if (!isAcceptedFile(file)) {
      setError('Tipo de ficheiro não suportado. Use PNG, JPG, JPEG, GIF ou WEBP.');
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      setFormValue(base64);
      setFileName(file.name);
      setError(null);
    } catch (err) {
      setError('Não foi possível ler o ficheiro.');
    }
  };

  const handleClear = () => {
    setFormValue('');
    setFileName(null);
    setError(null);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <input
        ref={inputRef}
        type='file'
        accept={ACCEPTED_EXTENSIONS.join(',')}
        style={{ display: 'none' }}
        onChange={handleChange}
        disabled={readonly}
      />
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <button
          type='button'
          className='btn btn-secondary'
          onClick={handlePick}
          disabled={readonly}
        >
          {base64Value ? 'Substituir ícone' : 'Escolher ícone'}
        </button>
        {base64Value && !readonly && (
          <button
            type='button'
            className='btn btn-link'
            onClick={handleClear}
          >
            Remover
          </button>
        )}
        {fileName && (
          <span style={{ color: '#666', fontSize: '0.85em' }}>{fileName}</span>
        )}
      </div>
      {error && (
        <div style={{ color: '#c92100', fontSize: '0.85em' }}>{error}</div>
      )}
      {base64Value && (
        <div
          style={{
            border: '1px solid #e0e0e0',
            borderRadius: '4px',
            padding: '8px',
            background: '#fafafa',
            width: 'fit-content'
          }}
        >
          <img
            src={base64Value}
            alt='Pré-visualização do ícone'
            style={{
              maxWidth: '160px',
              maxHeight: '160px',
              display: 'block'
            }}
          />
        </div>
      )}
    </div>
  );
};

export default IconUpload;
